import { BehaviorSubject, Subject, combineLatest } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { moveItemInArray } from '@angular/cdk/drag-drop';
import { NgForOfContext } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  Output,
  QueryList,
  SimpleChanges,
  TemplateRef,
  ViewChildren,
  ViewEncapsulation,
} from '@angular/core';

import { PhxDraggableListDeleteEvent, PhxDraggableListRearrangeEvent } from './phx-draggable-list.types';
import { Cross, Drag, Plus } from './../icon/icon-map';

export type IPhxDraggableListState = 'view' | 'edit';

export interface IPhxDraggableListItemSettings {
  phxSettings?: {
    deleteButtonDisabled?: boolean;
    deleteButtonDisabledTooltipText?: string;
  }
}
export interface IPhxList extends IPhxDraggableListItemSettings {
  [key: string]: any; // Index signature for any other keys
};

/**
 * To specify which element's height the handles should be anchored to, add the attribute [phx-alignment-anchor] to that element.
 */
@Component({
  selector: 'phx-draggable-list',
  templateUrl: './phx-draggable-list.component.html',
  styleUrls: ['./phx-draggable-list.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PhxDraggableListComponent<T extends IPhxList> {
  /** The list of drag handles (so that the correct handle can be focused while rearranging with keyboard). */
  @ViewChildren('dragHandle', { read: ElementRef })
  public dragHandles: QueryList<ElementRef<HTMLButtonElement>>;
  /** The list of item template wrappers (so that the correct item can be focused when the list is updated). */
  @ViewChildren('itemTemplateWrapper', { read: ElementRef })
  public itemTemplateWrappers: QueryList<ElementRef<HTMLDivElement>>;

  /** The list of objects to display. Do not update - instead use list$. */
  @Input() public readonly phxList: readonly T[] = [];
  /** The template for an object row. */
  @Input() public phxItemTemplate: TemplateRef<NgForOfContext<{ item: T; index: number; }>>;
  /** If 'view', display a static list; if 'edit', display an editable, reorganizable list. */
  @Input() public phxState: IPhxDraggableListState = 'view';
  /** Whether to show or hide the insert button. Do not update - instead use insertEnabled$. */
  @Input() public readonly phxInsertButtonVisible: boolean = false;
  /** Whether to disable the insert button. */
  @Input() public readonly phxInsertButtonDisabled: boolean = false;
  /** Custom tooltip for the insert button. */
  @Input() public readonly phxInsertButtonDisabledTooltipText: string = '';
  /** Whether to show or hide the delete button. */
  @Input() public readonly phxDeleteButtonVisible: boolean = false;
  /** Custom text for the insert button. */
  @Input() public phxInsertText: string;
  /** Emits when the list is rearranged. */
  @Output() public phxOnRearrange: EventEmitter<PhxDraggableListRearrangeEvent<T>> = new EventEmitter();
  /** Emits when the insert button is pressed. */
  @Output() public phxOnInsert: EventEmitter<void> = new EventEmitter();
  /** Emits when an item is deleted from the list. */
  @Output() public phxOnDelete: EventEmitter<PhxDraggableListDeleteEvent<T>> = new EventEmitter();

  /** Local stream copy of phxList input parameter. */
  public list$: BehaviorSubject<T[]> = new BehaviorSubject([...this.phxList]);
  /** Local stream copy of phxInsertButtonVisible input parameter. */
  public insertEnabled$: BehaviorSubject<boolean> = new BehaviorSubject(this.phxInsertButtonVisible);
  /** Currently-focused drag handle. */
  public dragHandleFocusIndex: number = null;
  /** Height of anchor element. */
  public alignmentAnchors: Array<{
    height: string;
    marginTop: string;
  }> = [];

  public readonly dragIcon: string = Drag;
  public readonly crossIcon: string = Cross;
  public readonly plusIcon: string = Plus;

  /** Emits once, on component destroyed. */
  private destroyed$: Subject<void> = new Subject();

  constructor(
    private cdr: ChangeDetectorRef,
    private elRef: ElementRef,
  ) { }

  public ngOnInit(): void {
    // When the list length drops to 0, if insert is enabled, insert a new entry.
    combineLatest([
      this.list$,
      this.insertEnabled$,
    ])
      .pipe(takeUntil(this.destroyed$))
      .subscribe(([list, insertEnabled]) => {
        if (list.length < 1 && insertEnabled) {
          this.insertHandler();
        }
        this.cdr.markForCheck();
      });
  }

  public ngAfterViewInit(): void {
    this.calculateAlignmentAnchors();
  }

  public ngAfterViewChecked(): void {
    this.calculateAlignmentAnchors();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.phxList) {
      // Update list.
      this.list$.next([...(changes.phxList.currentValue || [])]);

      // If there is a new item, focus on that item.
      const previous: T[] = changes.phxList.previousValue;
      const current: T[] = changes.phxList.currentValue;
      this.cdr.detectChanges();
      if (this.itemTemplateWrappers?.length > 0 && previous && current.length > previous.length) {
        this.focusOnFirstChild(
          Array.from(this.itemTemplateWrappers)[this.itemTemplateWrappers.length - 1].nativeElement,
        );
      }
    }

    if (changes.phxInsertButtonVisible) this.insertEnabled$.next(changes.phxInsertButtonVisible.currentValue);
  }

  public ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  /**
   * If a drag handle is focused, allow keyboard rearrangement.
   * @param event The keydown event.
   */
  @HostListener('document:keydown', ['$event'])
  public handleKeyboardEvent(event: KeyboardEvent): void {
    if (this.dragHandleFocusIndex === null) return;

    const previousIndex: number = this.dragHandleFocusIndex;
    const listLength: number = this.list$.getValue().length;
    let currentIndex: number = listLength + this.dragHandleFocusIndex;
    switch (event.key) {
      case 'ArrowUp':
        currentIndex = (currentIndex - 1) % listLength;
        break;
      case 'ArrowDown':
        currentIndex = (currentIndex + 1) % listLength;
        break;
      default:
        return;
    }
    this.dropHandler({ previousIndex, currentIndex });
    this.dragHandleFocusIndex = currentIndex;
    this.cdr.detectChanges();
    Array.from(this.dragHandles)[currentIndex].nativeElement.focus();
  }

  /**
   * Handle list rearrangement event.
   * Updates list order.
   * Emits on phxOnRearrange.
   * @param event The rearrangement event.
   */
  public dropHandler(event: {
    previousIndex: number;
    currentIndex: number;
  }): void {
    if (this.phxState !== 'edit') return;

    const list: T[] = this.list$.getValue();
    const previousArray: T[] = [...list];
    const item: T = list[event.previousIndex];
    moveItemInArray(list, event.previousIndex, event.currentIndex);

    this.phxOnRearrange.emit({
      previousArray,
      currentArray: list,
      previousIndex: event.previousIndex,
      currentIndex: event.currentIndex,
      item,
    });
    this.list$.next(list);
  }

  /**
   * Handle item deletion event.
   * Removes item from list.
   * Emits on phxOnDelete.
   * @param index The index of the deleted item.
   */
  public deleteHandler(index: number): void {
    if (!this.phxDeleteButtonVisible) return;

    const list: T[] = this.list$.getValue();
    const previousArray: T[] = [...list];
    const item: T = list[index];
    list.splice(index, 1);

    this.phxOnDelete.emit({
      previousArray,
      currentArray: list,
      index,
      item,
    });
    this.list$.next(list);
  }

  /**
   * Handle item insertion event.
   * Emits on phxOnInsert.
   */
  public insertHandler(): void {
    if (!this.insertEnabled$.getValue()) return;

    this.phxOnInsert.emit();
  }

  /**
   * Set the index of the focused drag handle.
   * @param index 
   */
  public dragHandleFocusHandler(index: number): void {
    this.dragHandleFocusIndex = index;
  }

  /**
   * Focus on an HTML element (or its first focusable child, depth-first).
   * @param element The HTML element to focus on.
   * @returns True if an element has received focus; false otherwise.
   */
  private focusOnFirstChild(element: HTMLElement): boolean {
    if (element.tabIndex > -1) {
      element.focus();
      return true;
    } else {
      for (let child of Array.from(element.children)) {
        if (this.focusOnFirstChild(child as HTMLElement)) {
          return true;
        }
      }
    }
    return false;
  }

  /**
   * Calculate the height and top offset of each alignment anchor.
   */
  private calculateAlignmentAnchors(): void {
    const anchors: HTMLElement[] = Array.from(this.elRef.nativeElement.querySelectorAll('.phx-draggable-list-row [phx-alignment-anchor]'));
    this.alignmentAnchors = anchors.map(anchor => ({
      height: anchor.clientHeight + 'px',
      marginTop: anchor.offsetTop - anchor.parentElement.offsetTop + 'px',
    }));
    this.cdr.markForCheck();
  }
}
